import Dexie, { type Table, type Transaction } from 'dexie';
import type {
  AccountRecord,
  ConversationAliasRecord,
  ConversationRecord,
  DownloadPartRecord,
  DraftRecord,
  EventSeenRecord,
  InviteRecord,
  MediaRecord,
  MemberRecord,
  MessageRecord,
  OutboxRecord,
  PinRecord,
  PollRecord,
  PreferenceRecord,
  ReactionRecord,
  SchemaMetadataRecord,
  StreamCursorRecord,
  TopicRecord,
  UploadRecord,
} from '@/store/types';

/**
 * The IndexedDB schema, exactly the object stores Section 14 lists, plus the
 * feature projections Phases 2–4 need to render offline.
 *
 * The compound indexes mirror the server's keyset queries so a local page and a
 * `GetHistory` page cover the same range in the same order:
 *
 *   - `messages` is keyed on `[conversationId+id]`, matching
 *     `chat.messages PK (conversation_id, id)`.
 *   - `[conversationId+orderSeq]` matches the server's
 *     `index conversation_id, created_seq DESC` and is the index the timeline
 *     pages backwards on.
 *   - `outbox` is keyed on `opId` alone, because the operation identifier is
 *     unique per installation and is the only key a retry may look up by.
 *   - `[conversationId+createdAt]` on the outbox is what makes per-conversation
 *     FIFO a range scan rather than a full-store sort.
 *
 * Dexie tables are exposed through a plain object rather than a `Dexie`
 * subclass with declared fields. With `useDefineForClassFields` enabled a
 * declared class field is emitted as a real property definition that would
 * overwrite the table object Dexie installs on the instance, so avoiding the
 * subclass avoids a whole class of silent breakage.
 */

/** Bumped whenever the stores or indexes below change. */
export const SCHEMA_VERSION = 3;

export const DATABASE_NAME = 'chat';

export interface ChatDatabase {
  readonly dexie: Dexie;
  readonly accounts: Table<AccountRecord, string>;
  readonly conversations: Table<ConversationRecord, string>;
  readonly messages: Table<MessageRecord, [string, string]>;
  readonly eventsSeen: Table<EventSeenRecord, [string, string]>;
  readonly streamCursors: Table<StreamCursorRecord, string>;
  readonly outbox: Table<OutboxRecord, string>;
  readonly drafts: Table<DraftRecord, string>;
  readonly uploads: Table<UploadRecord, string>;
  readonly media: Table<MediaRecord, string>;
  readonly downloadParts: Table<DownloadPartRecord, [string, string, number]>;
  readonly reactions: Table<ReactionRecord, [string, string, string, string]>;
  readonly pins: Table<PinRecord, [string, string]>;
  readonly members: Table<MemberRecord, [string, string]>;
  readonly topics: Table<TopicRecord, [string, string]>;
  readonly polls: Table<PollRecord, string>;
  readonly invites: Table<InviteRecord, [string, string]>;
  readonly conversationAliases: Table<ConversationAliasRecord, string>;
  readonly preferences: Table<PreferenceRecord, string>;
  readonly schemaMetadata: Table<SchemaMetadataRecord, number>;
}

/**
 * Fields added to existing records by Phases 2–4.
 *
 * A row written by the Phase 1 build has none of them, and `undefined` reaching
 * a component that expects a number is the sort of failure that shows up as a
 * blank screen rather than an error. Backfilling during the upgrade means the
 * rest of the client can treat these as always present.
 */
const CONVERSATION_DEFAULTS = {
  slowModeSeconds: 0,
  protectedContent: false,
  publicUsername: null,
  linkedDiscussionId: null,
  notificationMode: 'MENTIONS',
  subscribed: false,
  lastOwnSendAt: 0,
} as const;

const MESSAGE_DEFAULTS = {
  forwardOrigin: null,
  viewCount: 0,
  pinned: false,
} as const;

const DRAFT_DEFAULTS = {
  version: 0,
  pendingUploadIds: [] as string[],
  topicId: null,
  conflict: null,
} as const;

function defineDatabase(name: string): ChatDatabase {
  const dexie = new Dexie(name);

  dexie.version(1).stores({
    accounts: 'id',
    // `provisional` is deliberately not indexed: IndexedDB has no boolean key
    // type, so an index on it would silently store nothing and read as an
    // always-empty result.
    conversations: 'id, updatedAt',
    messages: '[conversationId+id], [conversationId+orderSeq], [conversationId+createdSeq], opId, tempId',
    events_seen: '[streamId+eventId], [streamId+seq]',
    stream_cursors: 'streamId',
    outbox: 'opId, state, nextAttemptAt, [conversationId+createdAt], [state+nextAttemptAt], tempId',
    drafts: 'conversationId, updatedAt',
    uploads: 'uploadId, conversationId',
    download_parts: '[mediaId+variant+offset]',
    preferences: 'key',
    schema_metadata: 'version',
  });

  dexie
    .version(2)
    .stores({
      // `state` is indexed on uploads so the transfer manager can find work to
      // resume after a reload with a range scan rather than a full table read.
      uploads: 'uploadId, conversationId, state, serverUploadId',
      media: 'mediaId',
      reactions: '[conversationId+messageId+userId+reaction], [conversationId+messageId]',
      pins: '[conversationId+messageId], conversationId',
      members: '[conversationId+userId], conversationId',
      topics: '[conversationId+topicId], conversationId',
      polls: 'pollId, conversationId',
      invites: '[conversationId+token], conversationId',
    })
    .upgrade(async (transaction: Transaction) => {
      await transaction
        .table<ConversationRecord, string>('conversations')
        .toCollection()
        .modify((record) => {
          Object.assign(record, { ...CONVERSATION_DEFAULTS, ...record });
        });
      await transaction
        .table<MessageRecord, [string, string]>('messages')
        .toCollection()
        .modify((record) => {
          Object.assign(record, { ...MESSAGE_DEFAULTS, ...record });
          // Content gained two references in Phase 3. The declared type says
          // they are present, but a row written by the Phase 1 build genuinely
          // has neither, so they are filled in individually rather than by a
          // spread the compiler would (correctly) say cannot change anything.
          record.content.topicId = record.content.topicId ?? null;
          record.content.embeddedId = record.content.embeddedId ?? null;
        });
      await transaction
        .table<DraftRecord, string>('drafts')
        .toCollection()
        .modify((record) => {
          Object.assign(record, { ...DRAFT_DEFAULTS, ...record });
        });
      await transaction
        .table<OutboxRecord, string>('outbox')
        .toCollection()
        .modify((record) => {
          // A Phase 1 operation carries no attachments by construction: the
          // phase had no way to create one.
          record.attachments = record.attachments ?? [];
        });
      // Phase 1 wrote a placeholder upload shape that never carried bytes and
      // was never resumable. Discarding those rows is correct: there is no file
      // to continue from, so keeping them would show progress rows that can
      // never finish.
      await transaction.table('uploads').clear();
      await transaction
        .table<SchemaMetadataRecord, number>('schema_metadata')
        .put({ version: SCHEMA_VERSION, appliedAt: Date.now() });
    });

  dexie
    .version(3)
    .stores({
      // Where a provisional conversation identifier ended up once the server
      // named the real one. Keyed by the provisional identifier because that is
      // what a stale reference holds and therefore what has to be looked up.
      conversation_aliases: 'provisionalId, canonicalId',
    })
    .upgrade(async (transaction: Transaction) => {
      // Nothing to backfill. Aliases only matter from the moment a conversation
      // is created, and any conversation that already exists locally has its
      // real identifier.
      await transaction
        .table<SchemaMetadataRecord, number>('schema_metadata')
        .put({ version: SCHEMA_VERSION, appliedAt: Date.now() });
    });

  // The applied schema version is recorded in its own store so a future
  // migration can tell "never opened" apart from "opened by an older build",
  // which Dexie's own version number alone does not distinguish once a user has
  // downgraded a tab.
  dexie.on('populate', (transaction: Transaction) => {
    void transaction
      .table<SchemaMetadataRecord, number>('schema_metadata')
      .put({ version: SCHEMA_VERSION, appliedAt: Date.now() });
  });

  return {
    dexie,
    accounts: dexie.table<AccountRecord, string>('accounts'),
    conversations: dexie.table<ConversationRecord, string>('conversations'),
    messages: dexie.table<MessageRecord, [string, string]>('messages'),
    eventsSeen: dexie.table<EventSeenRecord, [string, string]>('events_seen'),
    streamCursors: dexie.table<StreamCursorRecord, string>('stream_cursors'),
    outbox: dexie.table<OutboxRecord, string>('outbox'),
    drafts: dexie.table<DraftRecord, string>('drafts'),
    uploads: dexie.table<UploadRecord, string>('uploads'),
    media: dexie.table<MediaRecord, string>('media'),
    downloadParts: dexie.table<DownloadPartRecord, [string, string, number]>('download_parts'),
    reactions: dexie.table<ReactionRecord, [string, string, string, string]>('reactions'),
    pins: dexie.table<PinRecord, [string, string]>('pins'),
    members: dexie.table<MemberRecord, [string, string]>('members'),
    topics: dexie.table<TopicRecord, [string, string]>('topics'),
    polls: dexie.table<PollRecord, string>('polls'),
    invites: dexie.table<InviteRecord, [string, string]>('invites'),
    conversationAliases: dexie.table<ConversationAliasRecord, string>('conversation_aliases'),
    preferences: dexie.table<PreferenceRecord, string>('preferences'),
    schemaMetadata: dexie.table<SchemaMetadataRecord, number>('schema_metadata'),
  };
}

/**
 * The one database handle for this browsing context.
 *
 * Phase 1 signs in one account at a time, so a single database matches the
 * Section 14 store list exactly — `messages(conversation_id, id)` carries no
 * account column. Phase 5 multi-account isolation is a database *per account*
 * created from this same definition, which is why the definition is a function
 * of the name rather than a module-level singleton construction.
 */
export const db: ChatDatabase = defineDatabase(DATABASE_NAME);
